import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MyStatsResponse } from '@/types/my-stats';
import { DashboardSettings } from '@/pages/dashboard/types';
import { formatTimeForDisplay } from '@/pages/dashboard/utils';
import { Clock, Target, Calendar, BarChart3 } from 'lucide-react';
import { useHideAmounts } from '@/hooks/useHideAmounts';

interface DashboardStatisticsProps {
  dayStats: MyStatsResponse | null;
  monthStats: MyStatsResponse | null;
  yearStats: MyStatsResponse | null;
  isLoading: boolean;
  settings: DashboardSettings;
  selectedDate?: Date;
}

export function DashboardStatistics({
  dayStats,
  monthStats,
  yearStats,
  isLoading,
  settings,
  selectedDate
}: DashboardStatisticsProps) {
  const { hideAmounts, amountClassName } = useHideAmounts();
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Performance Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get the most important metrics
  const yearlyTarget = yearStats?.target_tracking?.yearly_target || 0;
  const ytdBillableHours = yearStats?.target_tracking?.ytd_billable_hours || 0;
  const targetProgress = yearStats?.target_tracking?.target_progress_percentage || 0;
  const hoursAheadBehind = yearStats?.target_tracking?.hours_ahead_behind || 0;

  // Time metrics - use actual values when available
  const todayBillableHours = dayStats?.time_metrics?.billable_hours_actual || 
                            dayStats?.time_metrics?.billable_hours || 0;
  const todayTotalHours = dayStats?.time_metrics?.total_hours_actual || 
                         dayStats?.time_metrics?.total_hours || 0;
  const monthBillableHours = monthStats?.time_metrics?.billable_hours_actual || 
                            monthStats?.time_metrics?.billable_hours || 0;
  const monthTotalHours = monthStats?.time_metrics?.total_hours_actual || 
                         monthStats?.time_metrics?.total_hours || 0;
  const yearBillableHours = yearStats?.time_metrics?.billable_hours_actual || 
                           yearStats?.time_metrics?.billable_hours || ytdBillableHours;
  const yearTotalHours = yearStats?.time_metrics?.total_hours_actual || 
                        yearStats?.time_metrics?.total_hours || 0;

  // Revenue metrics - use actual values when available
  const todayRevenue = dayStats?.financial_metrics?.billable_revenue_actual || 
                      dayStats?.financial_metrics?.billable_revenue || 0;
  const monthRevenue = monthStats?.financial_metrics?.billable_revenue_actual || 
                      monthStats?.financial_metrics?.billable_revenue || 0;
  const yearRevenue = yearStats?.financial_metrics?.billable_revenue_actual || 
                     yearStats?.financial_metrics?.billable_revenue || 0;
  
  // Check if revenue metrics are available (admin users)
  const hasRevenueMetrics = monthStats?.financial_metrics?.billable_revenue !== undefined;





  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-BE', {
      style: 'currency',
      currency: settings.currency || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get progress color based on percentage
  const getProgressColor = (percentage: number): string => {
    if (percentage >= 100) return 'text-green-600';
    if (percentage >= 80) return 'text-blue-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };





  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <BarChart3 className="h-5 w-5" />
          Performance Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Yearly Target Progress - Most Important */}
          <div className="p-4 rounded-lg border bg-muted/40">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold text-foreground">Yearly Target Progress</h3>
              </div>
              <div className={`text-2xl font-bold ${getProgressColor(targetProgress)}`}>
                {Math.round(targetProgress)}%
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    targetProgress >= 100 ? 'bg-green-500' : 
                    targetProgress >= 80 ? 'bg-blue-500' : 
                    targetProgress >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(targetProgress, 100)}%` }}
                />
              </div>
              
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>YTD Billable: {formatTimeForDisplay(ytdBillableHours, settings)}</span>
                <span>Target: {formatTimeForDisplay(yearlyTarget, settings)}</span>
                <span className={hoursAheadBehind >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {hoursAheadBehind >= 0 ? '+' : ''}{formatTimeForDisplay(Math.abs(hoursAheadBehind), settings)} 
                  {hoursAheadBehind >= 0 ? ' ahead' : ' behind'}
                </span>
              </div>
            </div>
          </div>

          {/* Transposed Statistics Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="py-3 px-4 text-left font-medium">Period</th>
                  <th className="py-3 px-4 text-center font-medium">Billable Hours</th>
                  <th className="py-3 px-4 text-center font-medium">Total Hours</th>
                  {hasRevenueMetrics && (
                    <th className="py-3 px-4 text-center font-medium">Revenue</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {/* Selected Day Row */}
                <tr className="border-b hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {selectedDate && selectedDate.toDateString() === new Date().toDateString() 
                          ? 'Today' 
                          : selectedDate 
                            ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : 'Selected Day'
                        }
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="font-semibold">
                      {formatTimeForDisplay(todayBillableHours, settings)}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="font-medium text-muted-foreground">
                      {formatTimeForDisplay(todayTotalHours, settings)}
                    </div>
                  </td>
                  {hasRevenueMetrics && (
                    <td className="py-3 px-4 text-center">
                      <div className={`font-semibold ${amountClassName}`}>
                        {hideAmounts ? '•••' : formatCurrency(todayRevenue)}
                      </div>
                    </td>
                  )}
                </tr>

                {/* Selected Month Row */}
                <tr className="border-b hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {selectedDate && new Date(selectedDate.getFullYear(), selectedDate.getMonth()).getTime() === new Date(new Date().getFullYear(), new Date().getMonth()).getTime()
                          ? 'This Month'
                          : selectedDate
                            ? selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                            : 'Selected Month'
                        }
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="font-semibold">
                      {formatTimeForDisplay(monthBillableHours, settings)}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="font-medium text-muted-foreground">
                      {formatTimeForDisplay(monthTotalHours, settings)}
                    </div>
                  </td>
                  {hasRevenueMetrics && (
                    <td className="py-3 px-4 text-center">
                      <div className={`font-semibold ${amountClassName}`}>
                        {hideAmounts ? '•••' : formatCurrency(monthRevenue)}
                      </div>
                    </td>
                  )}
                </tr>

                {/* Selected Year Row */}
                <tr className="border-b hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {selectedDate && selectedDate.getFullYear() === new Date().getFullYear()
                          ? 'This Year'
                          : selectedDate
                            ? selectedDate.getFullYear().toString()
                            : 'Selected Year'
                        }
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="font-semibold">
                      {formatTimeForDisplay(yearBillableHours, settings)}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="font-medium text-muted-foreground">
                      {formatTimeForDisplay(yearTotalHours, settings)}
                    </div>
                  </td>
                  {hasRevenueMetrics && (
                    <td className="py-3 px-4 text-center">
                      <div className={`font-semibold ${amountClassName}`}>
                        {hideAmounts ? '•••' : formatCurrency(yearRevenue)}
                      </div>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Additional Insights */}
          {monthStats && (
            <div className="pt-4 border-t">
              <div className="grid grid-cols-3 gap-4 text-center text-xs text-muted-foreground">
                <div>
                  <div className="font-medium">Days Worked</div>
                  <div>{monthStats.time_metrics.days_worked}</div>
                </div>
                <div>
                  <div className="font-medium">Efficiency</div>
                  <div>{Math.round(monthStats.productivity_metrics.efficiency_score)}%</div>
                </div>
                <div>
                  <div className="font-medium">Avg Daily</div>
                  <div>{formatTimeForDisplay(monthStats.time_metrics.average_billable_hours_per_day, settings)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}